/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import { AfterViewInit, Component } from '@angular/core';
import { AuditModel } from '../../../../shared/models';
import { AuditingApiService, OrganisationsApiService } from '../../../../shared/http';
import { BaseComponent } from '../../../../../../core/components';
import { DropdownModel } from '../../../../../../core/models';
import { LogService, MenuService, RbacService, UserService, UtilsService } from '../../../../../../core/services';
import { LoginModel } from '../../../../../loadbalancer/shared/models';
import { forkJoin } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
    selector: 'app-auditing',
    templateUrl: './auditing.component.html',
    styleUrls: ['./auditing.component.scss']
})
export class AuditingComponent extends BaseComponent implements AfterViewInit {
    /**
     * Has tier
     */
    public hasTier: boolean = false;

    /**
     * Table rows
     */
    public tableRows: AuditModel[] = [];

    /**
     * Search term for searching the services table, also used with the search query parameter in the url.
     */
    public searchTerm: string = '';

    /**
     * Dropdown content.
     */
    public dropdown: any = {
        rows: []
    };

    /**
     * Store the audit data from the API.
     * @private
     */
    private _storedAudit: any[];

    constructor(
        private auditingApiService: AuditingApiService,
        private logService: LogService,
        private menuService: MenuService,
        private organisationsApiService: OrganisationsApiService,
        private rbacService: RbacService,
        private userService: UserService,
        private utilsService: UtilsService
    ) {
        super();

        this.hasTier = this.rbacService.getPageRequiredRoute('/api/v1/organisations/:orgid/audit')?.tier;
        this.setUpPageData();
    }

    public ngAfterViewInit(): void {
        if (this.hasTier && !this.menuService.menuClosed) {
            this.menuService.menuClose.next(true);
        }
    }

    /**
     * Update table.
     * @param event
     */
    public updateTable(event: any): void {
        if (event.key === 'all') {
            this.tableRows = this._storedAudit;
        } else {
            const { start, end } = event?.options;
            const filteredRows: any[] = this._storedAudit.filter(item => item.rowNumber >= start && item.rowNumber <= end);
            this.tableRows = [...filteredRows];
        }
    }

    /**
     * Set up page data
     * @private
     */
    private setUpPageData(): void {
        const organisationId = this.userService.getUser('organisationId');

        const apiCalls = {
            audits: this.auditingApiService.getAll(organisationId),
            users: this.organisationsApiService.getMembers(organisationId)
        };

        forkJoin(apiCalls)
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (dataGroups: any): void => {
                    this.populateTableRows(dataGroups);
                },
                error: (error: any): void => {
                    this.logService.error('UsersListComponent: getMembers', error);
                }
            });
    }

    /**
     * Populate tables rows.
     * @private
     */
    private populateTableRows(dataGroups: any): void {
        this.tableRows = [];
        const reverseData = dataGroups.audits?.reverse();
        const tempRows: AuditModel[] = [];
        const rowSets: any[] = [];
        let index: number = 0;
        for (const audit of reverseData) {
            const user = dataGroups.users.find((item: LoginModel): boolean => item.id === audit.performedBy);
            tempRows.push(
                {
                    id: audit.id,
                    action: audit.action,
                    actionImageName: this.getImageByAction(audit.action),
                    performedBy: user?.firstName,
                    orgId: audit.orgID,
                    timestamp: this.utilsService.createDateTimeString(audit.timestamp),
                    details: audit.details,
                    rowNumber: index
                }
            );

            if (index !== 0 && Number.isInteger(index / 100)) {
                rowSets.push(this.getRowSets(reverseData, index));
            }

            index++;
        }

        this._storedAudit = tempRows;
        const { start, end } = rowSets[0].options;
        this.tableRows = this._storedAudit.filter(item => item.rowNumber >= start && item.rowNumber <= end);
        this.createRecordsDropdown(rowSets);
    }

    /**
     * Get row sets.
     * @param reverseData
     * @param index
     * @private
     */
    private getRowSets(reverseData: any, index: number): DropdownModel {
        const start: number = index - 100;
        const end: number = index - 1;
        const first = reverseData[start];
        const last = reverseData[end];
        const startDateTime: Date = new Date(first.timestamp);
        const endDateTime: Date = new Date(last.timestamp);
        const locale: string = Intl.DateTimeFormat().resolvedOptions().locale;
        const startDate: string = startDateTime.toLocaleString(locale, { month: 'short' });
        const endDate: string = endDateTime.toLocaleString(locale, { month: 'short' });
        const year: string = startDateTime.toLocaleString(locale, { year: 'numeric' });
        const date: string = startDate === endDate ? startDate : startDate + ' - ' + endDate;

        return { key: index, value: `${start} - ${end} (${date} ${year})`, options: { start, end } };
    }

    /**
     * Create records dropdown.
     * @param rowSets
     * @private
     */
    private createRecordsDropdown(rowSets: any[]): void {
        const { end } = rowSets[rowSets.length - 1].options;
        const lastEnd = end + 1;
        rowSets.push(<DropdownModel>{
            key: lastEnd,
            value: `${lastEnd} +`,
            options: { start: lastEnd, end: lastEnd + 100 }
        });

        rowSets.push(<DropdownModel>{
            key: 'all',
            value: 'All'
        });

        this.dropdown.rows = rowSets;
    }

    /**
     * Get image path for action.
     * @param action
     * @private
     */
    private getImageByAction(action: string): string {
        let actionImagePath: string;
        switch (action) {
            case 'account':
                actionImagePath = './assets/centralisedManagement/icons/account-settings.svg';
                break;
            case 'adcs':
                actionImagePath = './assets/centralisedManagement/icons/adc.svg';
                break;
            case 'backup':
                actionImagePath = './assets/centralisedManagement/icons/backup.svg';
                break;
            case 'schedule':
            case 'scheduler':
                actionImagePath = './assets/centralisedManagement/icons/schedule.svg';
                break;
            case 'system':
                actionImagePath = './assets/centralisedManagement/icons/settings.svg';
                break;
            case 'user':
            case 'organisations':
                actionImagePath = './assets/centralisedManagement/icons/users.svg';
                break;
            default:
                actionImagePath = null;
        }

        return actionImagePath;
    }
}
